import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Plus } from "lucide-react"

export interface CustomizeTrayWidgetDef {
  titleKey: TranslationKeys
}

export interface CustomizeTrayProps<K extends string> {
  hidden: K[]
  onShow: (key: K) => void
  /** Studio-specific widget registry — only `titleKey` is needed here. */
  widgets: Record<K, CustomizeTrayWidgetDef>
  titleKey: TranslationKeys
  emptyKey: TranslationKeys
}

export function CustomizeTray<K extends string>({
  hidden,
  onShow,
  widgets,
  titleKey,
  emptyKey,
}: CustomizeTrayProps<K>) {
  const { translate } = useTranslation()
  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 mt-[18px] rounded-xl border border-dashed border-grey4 p-5 duration-200">
      <p className="mb-3 text-xs font-medium text-grey5">
        {translate(titleKey)}
      </p>
      {hidden.length === 0 ? (
        <p className="text-xs text-grey5">{translate(emptyKey)}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {hidden.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => onShow(key)}
              className="inline-flex items-center gap-1.5 rounded-full border border-grey4 bg-grey2 px-3 py-1.5 text-xs font-medium text-grey5 transition-colors duration-150 hover:border-grey5 hover:bg-grey1 hover:text-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Plus size={12} />
              {translate(widgets[key].titleKey)}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
